import { useState } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Wrench,
  Clipboard,
  Download,
  Loader2,
  FileDown,
  Share2,
  Check,
  ChevronDown,
  RotateCcw,
  Ban,
  Bot,
  Copy,
  MoreHorizontal,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  GithubIcon as Github,
  LinearIcon,
  JiraIcon,
  AzureDevOpsIcon,
} from "@/components/icons";
import { type CanonicalState, OPEN_STATES } from "@/components/finding-badges";
import { type TriageAction, TriageActionDialog } from "./triage-action-dialog";
import { useTransitionFindings } from "./use-transition-findings";
import { CanEdit } from "@/lib/project-access";
import { isProblemType, type ProjectExportConfig } from "@iamu/shared";
import type { OpenWorkRequestRow } from "@iamu/db";
import { useWorkRequests } from "./use-work-requests";

interface FindingFixDropdownProps {
  findingId: string;
  projectId: string;
  hasLinkedRepo: boolean;
  size?: "default" | "sm" | "xs" | "lg";
  variant?: "default" | "outline";
  className?: string;
  state?: string | null;
  requests?: ReturnType<typeof useWorkRequests>;
  open?: OpenWorkRequestRow | null;
  assigneeName?: string | null;
}

function useFixActions({
  findingId,
  projectId,
  hasLinkedRepo,
  state,
}: {
  findingId: string;
  projectId: string;
  hasLinkedRepo: boolean;
  state?: string | null;
}) {
  const utils = trpc.useUtils();
  const transition = useTransitionFindings(projectId);

  const { data: trackerTarget } = trpc.workflow.integrationTarget.get.useQuery(
    { projectId },
    { staleTime: 60_000 },
  );

  const briefMutation = trpc.finding.suggestFix.useQuery(
    { findingId },
    { enabled: false, staleTime: 60_000 },
  );

  const createIssueMutation = trpc.finding.createIssue.useMutation({
    onSuccess: (data) => {
      const dataProv = (data as { provider?: string }).provider;
      const prov =
        dataProv ??
        (trackerTarget?.provider ? trackerTarget.provider : "github");
      const provName =
        prov === "linear"
          ? "Linear"
          : prov === "jira"
            ? "Jira"
            : prov === "azure_devops"
              ? "Azure DevOps"
              : "GitHub";
      toast.success(`${provName} issue created`, {
        action: data.issueUrl
          ? {
              label: "View issue",
              onClick: () => window.open(data.issueUrl, "_blank"),
            }
          : undefined,
      });
      if (state === "new") {
        transition.run(
          { ids: [findingId], to: "open" },
          "Accepted — moved to Ready to fix",
        );
      }
      utils.finding.list.invalidate({ projectId });
      utils.finding.queueCounts.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Issue creation failed"),
  });

  async function fetchBrief() {
    const result = await briefMutation.refetch();
    if (!result.data?.brief) {
      toast.info("No brief available for this finding");
      return null;
    }
    return result.data.brief;
  }

  async function handleCopyAsPrompt() {
    try {
      const brief = await fetchBrief();
      if (!brief) return;
      try {
        await navigator.clipboard.writeText(brief);
        toast.success("Brief copied to clipboard");
      } catch {
        toast.error("Failed to copy to clipboard");
      }
    } catch {
      toast.error("Failed to generate brief");
    }
  }

  async function handleDownloadBrief() {
    try {
      const brief = await fetchBrief();
      if (!brief) return;
      const blob = new Blob([brief], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finding-${findingId.slice(0, 8)}-brief.md`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
      toast.success("Brief downloaded");
    } catch {
      toast.error("Failed to generate brief");
    }
  }

  const activeProvider =
    trackerTarget?.provider ?? (hasLinkedRepo ? "github" : null);
  const isPending = createIssueMutation.isPending;

  function renderTrackerItem() {
    if (activeProvider === "linear") {
      return (
        <DropdownMenuItem
          onClick={() => createIssueMutation.mutate({ findingId })}
          disabled={isPending}
        >
          <LinearIcon className="mr-2 h-4 w-4" />
          {isPending ? "Creating..." : "Create Linear issue"}
        </DropdownMenuItem>
      );
    }
    if (activeProvider === "jira") {
      return (
        <DropdownMenuItem
          onClick={() => createIssueMutation.mutate({ findingId })}
          disabled={isPending}
        >
          <JiraIcon className="mr-2 h-4 w-4" />
          {isPending ? "Creating..." : "Create Jira issue"}
        </DropdownMenuItem>
      );
    }
    if (activeProvider === "azure_devops") {
      return (
        <DropdownMenuItem
          onClick={() => createIssueMutation.mutate({ findingId })}
          disabled={isPending}
        >
          <AzureDevOpsIcon className="mr-2 h-4 w-4" />
          {isPending ? "Creating..." : "Create Azure DevOps work item"}
        </DropdownMenuItem>
      );
    }
    if (activeProvider === "github") {
      return (
        <DropdownMenuItem
          onClick={() => createIssueMutation.mutate({ findingId })}
          disabled={isPending}
        >
          <Github className="mr-2 h-4 w-4" />
          {isPending ? "Creating..." : "Create GitHub issue"}
        </DropdownMenuItem>
      );
    }
    return (
      <DropdownMenuItem disabled>
        <Github className="mr-2 h-4 w-4" />
        Connect tracker to create issues
      </DropdownMenuItem>
    );
  }

  return {
    handleCopyAsPrompt,
    handleDownloadBrief,
    renderTrackerItem,
  };
}

function FindingFixDropdownInner({
  findingId,
  projectId,
  hasLinkedRepo,
  size = "sm",
  variant = "default",
  className,
  state,
  requests: requestsProp,
  open: openProp,
  assigneeName,
}: FindingFixDropdownProps) {
  const hookRequests = useWorkRequests(projectId);
  const requests = requestsProp ?? hookRequests;
  const transition = useTransitionFindings(projectId);
  const open =
    openProp !== undefined
      ? openProp
      : (requests.byCanonical.get(findingId) ?? null);

  const { handleCopyAsPrompt, handleDownloadBrief, renderTrackerItem } =
    useFixActions({ findingId, projectId, hasLinkedRepo, state });

  const assigneeTitle = `Picked up by ${
    assigneeName ? `${assigneeName}'s` : "any editor's"
  } agent on next sync`;

  const isOutline = Boolean(open) || variant === "outline";

  return (
    <div
      className={cn("inline-flex items-center -space-x-px", className)}
      onClick={(e) => e.stopPropagation()}
    >
      {open ? (
        <Button
          variant="outline"
          size={size}
          className="rounded-r-none"
          onClick={() => requests.cancel(open.id)}
          disabled={requests.isPending}
        >
          Cancel request
        </Button>
      ) : (
        <Button
          variant={variant}
          size={size}
          className="rounded-r-none"
          title={assigneeTitle}
          onClick={() => {
            requests.queue({ kind: "fix", canonicalId: findingId });
            if (state === "new") {
              transition.run(
                { ids: [findingId], to: "open" },
                "Accepted — moved to Ready to fix",
              );
            }
          }}
          disabled={requests.isPending}
        >
          <Wrench
            className={size === "xs" ? "size-3" : "size-4"}
            aria-hidden="true"
          />
          Queue fix
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={isOutline ? "outline" : "default"}
            size={size}
            className={cn(
              "px-1.5 rounded-l-none",
              !isOutline && "border-l border-primary-foreground/20",
            )}
            aria-label="More fix options"
          >
            <ChevronDown
              className={size === "xs" ? "size-3" : "size-3.5"}
              aria-hidden="true"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleCopyAsPrompt}>
            <Clipboard className="mr-2 h-4 w-4" />
            Copy as prompt
          </DropdownMenuItem>
          {renderTrackerItem()}
          <DropdownMenuItem onClick={handleDownloadBrief}>
            <Download className="mr-2 h-4 w-4" />
            Download brief
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface FindingTriageMenuProps {
  findingId: string;
  projectId: string;
  /** Canonical lifecycle state; null for raw observations */
  state?: string | null;
  /** Effective finding type — strengths only offer reopen / dismiss */
  findingType?: string | null;
  /** Title for the action dialogs */
  subject?: string;
  size?: "default" | "sm" | "xs" | "lg";
  className?: string;
  iconOnly?: boolean;
  hasLinkedRepo?: boolean;
  includeFixActions?: boolean;
  requests?: ReturnType<typeof useWorkRequests>;
  open?: OpenWorkRequestRow | null;
  assigneeName?: string | null;
}

/**
 * Triage menu for one canonical finding (CONTRACT §1). Moves that need
 * a word (not relevant, walker error, duplicate, snooze, accept risk)
 * open `TriageActionDialog`; the rest apply at once with an undo toast.
 * Verified and regressed are never offered — only a re-run sets them.
 */
function FindingTriageMenuInner({
  findingId,
  projectId,
  state,
  findingType,
  subject,
  size = "sm",
  className,
  iconOnly = false,
  hasLinkedRepo = false,
  includeFixActions = false,
  requests: requestsProp,
  open: openProp,
  assigneeName,
}: FindingTriageMenuProps) {
  const [action, setAction] = useState<TriageAction | null>(null);
  const transition = useTransitionFindings(projectId);
  const current = (state ?? "open") as CanonicalState;
  const problem = isProblemType(findingType);
  const isOpenish = OPEN_STATES.includes(current);
  const canFix =
    problem &&
    current !== "fix_applied" &&
    current !== "verified" &&
    current !== "dismissed";

  const hookRequests = useWorkRequests(projectId);
  const requests = requestsProp ?? hookRequests;
  const open =
    openProp !== undefined
      ? openProp
      : (requests?.byCanonical.get(findingId) ?? null);

  const fixActions = useFixActions({
    findingId,
    projectId,
    hasLinkedRepo,
    state,
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {iconOnly ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className={cn(
                // Match the height of the row's other xs buttons.
                size === "xs" ? "size-7" : "size-8",
                "text-muted-foreground hover:text-foreground",
                className,
              )}
              disabled={transition.isPending}
              aria-label="More actions"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size={size}
              className={className}
              disabled={transition.isPending}
              onClick={(e) => e.stopPropagation()}
            >
              Triage
              <ChevronDown
                className={size === "xs" ? "size-3" : "size-3.5"}
                aria-hidden="true"
              />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {includeFixActions && problem && canFix && (
            <>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Wrench className="mr-2 h-4 w-4" />
                  Fix this
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {requests &&
                    (open ? (
                      <DropdownMenuItem
                        onClick={() => requests.cancel(open.id)}
                        disabled={requests.isPending}
                      >
                        Cancel fix request
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => {
                          requests.queue({
                            kind: "fix",
                            canonicalId: findingId,
                          });
                          if (current === "new") {
                            transition.run(
                              { ids: [findingId], to: "open" },
                              "Accepted — moved to Ready to fix",
                            );
                          }
                        }}
                        disabled={requests.isPending}
                      >
                        <Wrench className="mr-2 h-4 w-4" />
                        Queue fix
                      </DropdownMenuItem>
                    ))}
                  <DropdownMenuItem onClick={fixActions.handleCopyAsPrompt}>
                    <Clipboard className="mr-2 h-4 w-4" />
                    Copy as prompt
                  </DropdownMenuItem>
                  {fixActions.renderTrackerItem()}
                  <DropdownMenuItem onClick={fixActions.handleDownloadBrief}>
                    <Download className="mr-2 h-4 w-4" />
                    Download brief
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
            </>
          )}
          {current === "new" && !iconOnly && (
            <DropdownMenuItem
              onClick={() =>
                transition.run(
                  { ids: [findingId], to: "open" },
                  "Accepted — moved to Ready to fix",
                )
              }
            >
              <Check className="mr-2 h-4 w-4" />
              Accept
            </DropdownMenuItem>
          )}
          {!isOpenish && (
            <DropdownMenuItem
              onClick={() =>
                transition.run(
                  { ids: [findingId], to: "open", reason: "reopened" },
                  "Reopened",
                )
              }
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reopen
            </DropdownMenuItem>
          )}
          {current !== "dismissed" && (
            <>
              {(current === "new" && !iconOnly) || !isOpenish ? (
                <DropdownMenuSeparator />
              ) : null}
              <DropdownMenuItem
                onClick={() =>
                  transition.run(
                    {
                      ids: [findingId],
                      to: "dismissed",
                      reason: "not_relevant",
                    },
                    "Dismissed",
                  )
                }
              >
                <Ban className="mr-2 h-4 w-4" />
                Dismiss
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAction("dismiss")}>
                <Bot className="mr-2 h-4 w-4" />
                Dismiss with note…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAction("duplicate")}>
                <Copy className="mr-2 h-4 w-4" />
                Duplicate of…
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {action && (
        <TriageActionDialog
          action={action}
          ids={[findingId]}
          projectId={projectId}
          subject={subject}
          onClose={() => setAction(null)}
        />
      )}
    </>
  );
}

interface BatchExportButtonProps {
  selectedIds: string[];
  projectId?: string;
  exportConfig?: ProjectExportConfig;
  hasLinkedRepo?: boolean;
}

export function BatchExportButton({ selectedIds }: BatchExportButtonProps) {
  const batchBriefQuery = trpc.finding.batchBrief.useQuery(
    { findingIds: selectedIds },
    { enabled: false, staleTime: 30_000 },
  );

  const isPending = batchBriefQuery.isFetching;

  async function handleCopy() {
    try {
      const result = await batchBriefQuery.refetch();
      if (!result.data?.markdown) {
        toast.info("No briefs available for selected findings");
        return;
      }
      try {
        await navigator.clipboard.writeText(result.data.markdown);
        toast.success(`${result.data.count} briefs copied to clipboard`);
      } catch {
        toast.error("Failed to copy to clipboard");
      }
    } catch {
      toast.error("Failed to generate briefs");
    }
  }

  async function handleDownload() {
    try {
      const result = await batchBriefQuery.refetch();
      if (!result.data?.markdown) {
        toast.info("No briefs available for selected findings");
        return;
      }
      const blob = new Blob([result.data.markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `findings-brief-${selectedIds.length}.md`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
      toast.success(`${result.data.count} briefs downloaded`);
    } catch {
      toast.error("Failed to generate briefs");
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={isPending}>
          {isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="mr-2 h-4 w-4" />
          )}
          Export selected
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleCopy}>
          <Clipboard className="mr-2 h-4 w-4" />
          Copy all as prompt
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDownload}>
          <Download className="mr-2 h-4 w-4" />
          Download briefs
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Export to external tool ──────────────────────────────────────

interface ExportFindingButtonProps {
  findingId: string;
  projectId: string;
  exportConfig?: ProjectExportConfig;
  hasLinkedRepo: boolean;
}

/** Hidden for project viewers — the API refuses their writes. */
export function FindingFixDropdown(props: FindingFixDropdownProps) {
  return (
    <CanEdit>
      <FindingFixDropdownInner {...props} />
    </CanEdit>
  );
}

/** Hidden for project viewers — the API refuses their writes. */
export function FindingTriageMenu(props: FindingTriageMenuProps) {
  return (
    <CanEdit>
      <FindingTriageMenuInner {...props} />
    </CanEdit>
  );
}

/** No external auto-fix tools are currently enabled. */
export function ExportFindingButton(_props: ExportFindingButtonProps) {
  return null;
}
